import api from './api';

export const queryService = {
  /**
   * Fetch paginated queries with optional filters.
   * @param {object} params Query parameters such as page, limit, status, etc.
   * @returns {Promise<{queries: Array, pagination: object}>}
   */
  async getQueries(params = {}) {
    const response = await api.get('/queries', { params });
    return response.data.data;
  },

  /**
   * Fetch aggregated query summary statistics.
   * @param {object} params Optional query parameters to scope the summary.
   * @returns {Promise<object>}
   */
  async getQuerySummary(params = {}) {
    const response = await api.get('/queries/stats/summary', { params });
    return response.data.data;
  },

  /**
   * Fetch queries assigned to the current user (or a provided assignee).
   * @param {object} params Query parameters such as page, limit, status, etc.
   * @returns {Promise<{queries: Array, pagination: object}>}
   */
  async getAssignedQueries(params = {}) {
    const response = await api.get('/queries/assigned', { params });
    return response.data.data;
  },

  /**
   * Fetch queries where the current user is in CC (notified for oversight).
   * @param {object} params Query parameters such as page, limit, status, etc.
   * @returns {Promise<{queries: Array, pagination: object}>}
   */
  async getNotifiedQueries(params = {}) {
    const response = await api.get('/queries/notified', { params });
    return response.data.data;
  },

  /**
   * Fetch all queries by paginating under the hood.
   * @param {object} params Filters to apply to the query list.
   * @param {number} pageSize Number of records to fetch per API call.
   * @param {number} maxPages Safety limit to avoid unbounded loops.
   * @returns {Promise<Array>}
   */
  async getAllQueries(params = {}, pageSize = 100, maxPages = 50) {
    let page = 1;
    let totalPages = 1;
    const allQueries = [];

    while (page <= totalPages && page <= maxPages) {
      const response = await api.get('/queries', {
        params: {
          ...params,
          page,
          limit: pageSize,
        },
      });

      const { queries = [], pagination } = response.data.data || {};
      allQueries.push(...queries);

      if (!pagination) {
        break;
      }

      totalPages = pagination.pages || page;

      if (queries.length === 0) {
        break;
      }

      page += 1;
    }

    return allQueries;
  },

  /**
   * Fetch a single query by ID.
   * @param {string} queryId
   * @returns {Promise<object>}
   */
  async getQueryById(queryId) {
    const response = await api.get(`/queries/${queryId}`);
    return response.data.data?.query;
  },

  /**
   * Fetch combined audit + thread timeline events for a query.
   * @param {string} queryId
   * @returns {Promise<{timeline: Array, counts: object}>}
   */
  async getQueryTimeline(queryId) {
    const response = await api.get(`/queries/${queryId}/timeline`);
    return response.data.data || { timeline: [], counts: {} };
  },

  /**
   * Add a response message to a query.
   * @param {string} queryId
   * @param {object|FormData} payload - Can be an object with message/attachments or FormData for file uploads
   * @param {object} [config] - Optional axios config (e.g., headers for multipart/form-data)
   * @param {boolean} [isDraft=false] - Whether this is a draft response (not visible to raiser)
   * @returns {Promise<object>}
   */
  async respondToQuery(queryId, payload, config = {}, isDraft = false) {
    // If payload is FormData, we need to let axios set Content-Type automatically with boundary
    // The default 'application/json' header from axios instance will interfere
    if (payload instanceof FormData) {
      // Add isDraft flag to FormData
      payload.append('isDraft', isDraft.toString());
      
      const finalConfig = {
        ...config,
        headers: {
          ...config.headers,
        },
      };
      // Use transformRequest to delete Content-Type so axios can set it properly for FormData
      if (!finalConfig.transformRequest) {
        finalConfig.transformRequest = [(data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }];
      }
      const response = await api.put(`/queries/${queryId}/respond`, payload, finalConfig);
      return response.data.data?.query;
    }
    // For JSON payload, add isDraft flag
    const finalPayload = {
      ...payload,
      isDraft: isDraft,
    };
    const response = await api.put(`/queries/${queryId}/respond`, finalPayload, config);
    return response.data.data?.query;
  },

  /**
   * Update lifecycle status for a query.
   * @param {string} queryId
   * @param {object} payload
   * @param {string} payload.status Target lifecycle status (e.g. raised, escalated)
   * @param {string} [payload.description] Optional reason/note
   * @param {string} [payload.escalatedToId] Optional escalation target
   * @returns {Promise<object>} Updated query
   */
  async updateQueryStatus(queryId, payload = {}) {
    if (!payload || typeof payload !== 'object') {
      throw new Error('Payload must be an object when updating query status');
    }
    const response = await api.put(`/queries/${queryId}/status`, payload);
    return response.data.data?.query;
  },

  /**
   * Save or update a draft response (files are uploaded to server immediately).
   * @param {string} queryId
   * @param {object|FormData} payload - message + optional files
   * @param {string} [draftThreadId] - existing draft thread ID to update
   * @returns {Promise<{query: object, draftThreadId: string}>}
   */
  async saveDraftResponse(queryId, { message, attachments = [], draftThreadId }) {
    const formData = new FormData();
    formData.append('message', message.trim());
    if (draftThreadId) {
      formData.append('draftThreadId', draftThreadId);
    }
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });

    const config = {
      transformRequest: [(data, headers) => {
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        return data;
      }],
    };

    const response = await api.put(`/queries/${queryId}/draft-response`, formData, config);
    return {
      query: response.data.data?.query,
      draftThreadId: response.data.data?.draftThreadId,
    };
  },

  /**
   * Delete a draft response.
   * @param {string} queryId
   * @param {string} threadId - The thread entry _id of the draft
   * @returns {Promise<object>} Updated query
   */
  async deleteDraftResponse(queryId, threadId) {
    const response = await api.delete(`/queries/${queryId}/draft-response/${threadId}`);
    return response.data.data?.query;
  },
};


